// Scherm 13 — het Funding-tabblad.
//
// Was het laatste "binnenkort"-scherm van de zeven. Sinds het werkblok
// FUNDING-LIJN draait het op echte data: 119 regelingen uit een vast register,
// waarvan wij de officiële pagina's zelf scrapen en lezen.
//
// V1-VORM (r. 367-535: filterkaart, sorteerrij, tabelkaart, inschuivend
// detailpaneel) met vier verbeteringen die uit de gemeten data komen:
//
//  1. DE VENSTERBADGE HEEFT VIER STANDEN in plaats van één deadline-datum.
//     De echte data kent doorlopend (28), een venster met start én eind (11),
//     alleen een einddatum (8) — en 72 regelingen waarvan de officiële pagina
//     géén aanvraagperiode noemt. Die laatste krijgen een neutrale badge, geen
//     groen "doorlopend": dat zou een belofte zijn die de bron niet doet.
//  2. KPI-RIJ boven de tabel die MEEBEWEEGT met de filters, zoals op Bronnen.
//     `zonder periode` en `zonder status` staan er bewust in: dat zijn
//     metingen, geen gebreken, en ze horen zichtbaar te zijn.
//  3. FILTERS OP THEMA EN VERSTREKKER naast sector — de assen die de data wél
//     draagt. Er is geen skill-filter, want er is geen skill-koppeling: een
//     regeling financiert scholing in het algemeen.
//  4. Het detailpaneel zet DE DOELGROEP-EIS BOVENAAN en sluit af met de
//     HERKOMSTREGEL (zie FundingDetail.tsx).
//
// DE TELREGEL VAN HET HUIS: kop, KPI-rij, menu en rijen komen uit één telling
// aan de serverkant. Let op de sector-as — een sectorbrede regeling telt in
// élke sector mee, dus het menu zegt bij maakindustrie 35 en niet 25, en de
// kop zegt hetzelfde.

import { useEffect, useState } from "react";

import { EmptyList, LoadError } from "../components/Empty";
import { FilterChips, type Chip } from "../components/FilterChips";
import { FilterMenu } from "../components/FilterMenu";
import { FundingDetail } from "../components/FundingDetail";
import { geenSectorTekst, SCOPE_UITLEG, scopeLabel, vondstLabel } from "../lib/fundingVondst";
import {
  Card,
  FilterBar,
  PageHeader,
  Pager,
  SearchField,
  SlidePanel,
  SortSelect,
  TableHead,
} from "../components/ui";
import type { Funding } from "../contracts";
import { useFunding, type FundingFilters } from "../lib/funding";
import { registerSectoren, useMij } from "../lib/mij";
import { useDebounced } from "../lib/useResource";
import { alsLijst, alsTekst, type Voorzet } from "../lib/voorzet";
import { DekkingLabel } from "../components/FundingAanvulling";
import { statusRegel, vensterStand } from "../lib/venster";
import { color, size, weight } from "../tokens";

const COLS = "1fr 150px 110px 150px 150px";
const LABELS = ["Regeling", "Verstrekker", "Max bedrag", "Aanvraagperiode", "Thema"] as const;
const PAGE_SIZE = 20;

type AsNaam = "sector" | "thema" | "verstrekkerType" | "status" | "venster" | "aanvraagmoment";

/** Welke as welk menu wordt, in de volgorde van de vraag die je het eerst stelt. */
const MENUS: { as: AsNaam; facet: string; label: string; uitleg: string }[] = [
  {
    as: "sector",
    facet: "sector",
    label: "Sector",
    uitleg:
      "Voor welke sector is de regeling bedoeld. Sectorbrede regelingen (SLIM, " +
      "Praktijkleren, WVA, LKS) tellen in ELKE sector mee — daarom is het getal " +
      "hoger dan het aantal regelingen dat alleen die sector bedient.",
  },
  {
    as: "thema",
    facet: "thema",
    label: "Thema",
    uitleg:
      "Waarvoor je het geld kunt gebruiken: bbl-plekken, diplomabonus, stages, " +
      "taal, praktijkbegeleiding. Dit vervangt de vaardigheden-koppeling uit de " +
      "oude opzet: regelingen financieren scholing in het algemeen, niet een " +
      "bepaalde vaardigheid.",
  },
  {
    as: "verstrekkerType",
    facet: "verstrekkerType",
    label: "Verstrekker",
    uitleg:
      "Wie het geld geeft. 102 van de 119 zijn O&O- of sectorfondsen — daar moet " +
      "je werkgever bij aangesloten zijn. De landelijke regelingen en de fiscale " +
      "regeling staan open voor iedereen die aan de voorwaarden voldoet.",
  },
  {
    as: "venster",
    facet: "vensterSoort",
    label: "Aanvraagperiode",
    uitleg:
      "Doorlopend, een venster met begin- en einddatum, of alleen een einddatum. " +
      "'Onbekend' betekent dat de officiële pagina er niets over zegt — wij vullen " +
      "dat niet in.",
  },
  {
    as: "status",
    facet: "status",
    label: "Status",
    uitleg:
      "Open, aangekondigd of gesloten. Officiële pagina's zeggen dit zelden met " +
      "zoveel woorden; waar de pagina zwijgt en er wel een aanvraagperiode staat, " +
      "rekenen wij de status daaruit en zegt het detailpaneel dat erbij.",
  },
  {
    as: "aanvraagmoment",
    facet: "aanvraagmoment",
    label: "Aanvragen",
    uitleg:
      "Vooraf of achteraf. Praktisch het belangrijkste veld na de doelgroep: wie " +
      "te laat aanmeldt bij een 'vooraf'-regeling loopt het geld mis.",
  },
];

// Betrouwbaar register (owner-go 08-10-2026, "volg advies"): de gebruiker ziet
// standaard wat bruikbaar is. "Bevestigd" = de officiële pagina bevestigt het;
// "Waarschijnlijk" = de actuele pagina van het fonds beschrijft de regeling,
// maar zonder datum of zonder letterlijk 'open'; "Niet zeker of gesloten"
// staat ingeklapt. Labels en uitleg komen van de server (config).
const TRAPPEN = ["bevestigd", "waarschijnlijk", "niet_zeker"] as const;
type Trap = (typeof TRAPPEN)[number];
const STANDAARD_TRAPPEN: Trap[] = ["bevestigd", "waarschijnlijk"];
const TRAP_KLEUR: Record<Trap, { tekst: string; vlak: string }> = {
  bevestigd: { tekst: color.accentGreen, vlak: "#ecfdf5" },
  waarschijnlijk: { tekst: color.accentAmber, vlak: color.accentAmberBg },
  niet_zeker: { tekst: color.soft, vlak: color.surfaceQuote },
};
const TRAP_TITEL: Record<Trap, string> = {
  bevestigd: "Bevestigd",
  waarschijnlijk: "Waarschijnlijk",
  niet_zeker: "Niet zeker of gesloten",
};
const TRAP_TELLER: Record<Trap, "bevestigd" | "waarschijnlijk" | "nietZeker"> = {
  bevestigd: "bevestigd",
  waarschijnlijk: "waarschijnlijk",
  niet_zeker: "nietZeker",
};

const SORTEER: [NonNullable<FundingFilters["sorteer"]>, string][] = [
  ["venster", "Aanvraagperiode"],
  ["bedrag", "Bedrag"],
  ["naam", "Naam"],
];

function Kpi({ n, label, kleur }: { n: number; label: string; kleur?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 120 }}>
      <div
        style={{
          // Zelfde maatvoering als ReeksWidget op de Arbeidsmarkt-tab: 25/700,
          // -0.02em, tabellarische cijfers. De owner wees die widgets aan als
          // referentie (opname 0:07).
          fontSize: 25,
          fontWeight: weight.bold,
          color: kleur ?? color.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {n.toLocaleString("nl-NL")}
      </div>
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function FundingPage({
  voorzet,
}: {
  /** W5: een filter van buiten (de skill-detail-link "alle N op de Funding-tab").
   *  Een nieuw object = opnieuw toepassen, zoals op de andere tabbladen. */
  voorzet?: Voorzet | null;
} = {}) {
  const [gekozen, setGekozen] = useState<Partial<Record<AsNaam, string[]>>>({});
  const [zoek, setZoek] = useState("");
  const [trappen, setTrappen] = useState<Trap[]>(STANDAARD_TRAPPEN);
  useEffect(() => {
    if (!voorzet) return;
    // Een voorzet zonder trap (de doorklik "alle N op de Funding-tab") telt
    // over alle trappen; anders klopt het getal van de doorklik niet meer.
    const gevraagd = alsLijst(voorzet, "trap").filter((t): t is Trap =>
      (TRAPPEN as readonly string[]).includes(t));
    setTrappen(gevraagd.length ? gevraagd : [...TRAPPEN]);
    const assen: Partial<Record<AsNaam, string[]>> = {};
    for (const menu of MENUS) {
      const waarden = alsLijst(voorzet, menu.as);
      if (waarden.length) assen[menu.as] = waarden;
    }
    setGekozen(assen);
    setZoek(alsTekst(voorzet, "zoek"));
    setPage(1);
    setOpen(null);
  }, [voorzet]);
  const [sorteer, setSorteer] = useState<FundingFilters["sorteer"]>("venster");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Funding | null>(null);
  const zoekTraag = useDebounced(zoek);
  const mij = useMij();
  // 02-11-2026: een pilotpropositie opent op de bedrijfstakken van haar school,
  // zoals de nieuwe weergave; ICT blijft zonder filter openen. Een voorzet wint.
  const eigen = registerSectoren(mij.stand === "ingelogd" ? mij.mij : null).join("|");
  useEffect(() => {
    if (!eigen || eigen === "ict" || voorzet) return;
    setGekozen((g) => (g.sector ? g : { ...g, sector: eigen.split("|") }));
  }, [eigen, voorzet]);

  const lijst = useFunding({
    ...gekozen,
    zoek: zoekTraag,
    sorteer,
    page,
    pageSize: PAGE_SIZE,
    trap: trappen,
  });
  const trapTellingen = lijst.data?.trappen;

  const data = lijst.data;
  const items = data?.items ?? [];
  const totaal = data?.total ?? 0;
  const kpis = data?.kpis;
  const assen = data?.assen;
  const totalPages = Math.max(1, Math.ceil(totaal / PAGE_SIZE));

  // key <-> label per as, uit de API. Het scherm zet nooit zelf een label:
  // één as, één woordenlijst, één plek.
  const labelKaart: Record<string, Record<string, string>> = {};
  for (const [naam, waarden] of Object.entries(assen ?? {})) {
    labelKaart[naam] = Object.fromEntries((waarden ?? []).map((w) => [w.key, w.label]));
  }

  function optiesVan(menu: (typeof MENUS)[number]) {
    const facetten = data?.facets?.[menu.facet as keyof typeof data.facets] ?? [];
    const labels = labelKaart[menu.as] ?? {};
    const heen: Record<string, string> = {};
    const terug: Record<string, string> = {};
    const counts: Record<string, number> = {};
    const options: string[] = [];
    for (const f of facetten) {
      const label = f.label || labels[f.value] || f.value;
      options.push(label);
      heen[f.value] = label;
      terug[label] = f.value;
      counts[label] = f.count;
    }
    return { options, heen, terug, counts };
  }

  const chips: Chip[] = MENUS.flatMap((menu) => {
    const o = optiesVan(menu);
    return (gekozen[menu.as] ?? []).map((waarde) => ({
      axis: menu.as,
      value: waarde,
      label: `${menu.label}: ${o.heen[waarde] ?? labelKaart[menu.as]?.[waarde] ?? waarde}`,
    }));
  });

  function wis(chip: Chip) {
    const as = chip.axis as AsNaam;
    setGekozen((g) => ({ ...g, [as]: (g[as] ?? []).filter((v) => v !== chip.value) }));
    setPage(1);
  }

  const kop = lijst.loading && !data
    ? "laden…"
    : `${totaal.toLocaleString("nl-NL")} ${totaal === 1 ? "regeling" : "regelingen"}` +
      (chips.length || zoekTraag
        ? " die aan het filter voldoen"
        : trapTellingen && trappen.length < TRAPPEN.length
          ? ` (${trappen.map((t) => TRAP_TITEL[t].toLowerCase()).join(" en ")})`
          : " in het register");

  return (
    <div>
      <PageHeader title="Funding" count={kop} />

      {/* De filterbalk staat DIRECT onder de witte kopbalk (owner-opname 0:35,
          "filters altijd direct onder de witte kopbalk, zoals op de andere
          tabs"). De KPI-rij zakt daarmee onder de filters — dat klopt ook
          inhoudelijk: die getallen bewegen mee met wat je hierboven kiest. */}
      <FilterBar
        chips={
          chips.length > 0 ? (
            <FilterChips
              chips={chips}
              onRemove={wis}
              onClearAll={() => {
                setGekozen({});
                setPage(1);
              }}
            />
          ) : null
        }
      >
          <SearchField
            value={zoek}
            onChange={(v) => {
              setZoek(v);
              setPage(1);
            }}
            placeholder="Zoek regeling of fonds…"
          />
          <div style={{ width: 1, height: 24, background: color.border, flexShrink: 0 }} />
          {MENUS.map((menu) => {
            const o = optiesVan(menu);
            return (
              <FilterMenu
                key={menu.as}
                label={menu.label}
                note={menu.uitleg}
                options={o.options}
                values={(gekozen[menu.as] ?? []).map((v) => o.heen[v] ?? v)}
                counts={o.counts}
                onChange={(labels: string[]) => {
                  setGekozen((g) => ({
                    ...g,
                    [menu.as]: labels.map((l) => o.terug[l]!).filter(Boolean),
                  }));
                  setPage(1);
                }}
              />
            );
          })}
      </FilterBar>

      {/* Betrouwbaar register: de drie trappen als keuze, met het aantal. */}
      {trapTellingen && (
        <div
          data-testid="trappen"
          role="group"
          aria-label="Hoe zeker is de regeling"
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}
        >
          {TRAPPEN.map((t) => {
            const aan = trappen.includes(t);
            const k = TRAP_KLEUR[t];
            return (
              <button
                key={t}
                type="button"
                aria-pressed={aan}
                data-trap={t}
                onClick={() => {
                  setTrappen((nu) => {
                    const volgende = nu.includes(t) ? nu.filter((x) => x !== t) : [...nu, t];
                    return volgende.length ? volgende : [t];
                  });
                  setPage(1);
                }}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  padding: "8px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                  border: `1px solid ${aan ? k.tekst : color.border}`,
                  background: aan ? k.vlak : "transparent",
                  color: aan ? k.tekst : color.soft,
                  fontSize: size.meta,
                  fontWeight: weight.medium,
                }}
              >
                <span style={{ fontSize: size.body, fontWeight: weight.bold, fontVariantNumeric: "tabular-nums" }}>
                  {trapTellingen[TRAP_TELLER[t]]}
                </span>
                {TRAP_TITEL[t]}
              </button>
            );
          })}
          <span style={{ fontSize: size.label, color: color.faint, alignSelf: "center" }}>
            {trappen.includes("niet_zeker")
              ? "Je ziet ook regelingen die gesloten zijn of waarvan we geen actuele pagina vonden."
              : "Bevestigd: de pagina van de verstrekker bevestigt het. Waarschijnlijk: de actuele pagina beschrijft de regeling, maar zonder datum. Ga dat na bij de verstrekker."}
          </span>
        </div>
      )}

      {/* 2 — de KPI-rij, meebewegend met de filters. */}
      {kpis && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <Kpi n={kpis.totaal} label="in beeld" />
            <Kpi n={kpis.doorlopend} label="doorlopend" kleur={color.accentGreen} />
            <Kpi n={kpis.metVenster} label="met een aanvraagperiode" />
            <Kpi
              n={kpis.sluitBinnen}
              label={`sluit binnen ${kpis.vensterDagen ?? 60} dagen`}
              kleur={kpis.sluitBinnen ? color.accentRed : undefined}
            />
            <Kpi
              n={kpis.opentBinnen}
              label={`opent binnen ${kpis.vensterDagen ?? 60} dagen`}
              kleur={kpis.opentBinnen ? color.accentAmber : undefined}
            />
            <Kpi n={kpis.zonderVenster} label="zonder periode op de pagina" kleur={color.soft} />
          </div>
          <p
            style={{
              fontSize: size.label,
              color: color.faint,
              margin: "12px 0 0",
              lineHeight: 1.6,
            }}
          >
            De laatste twee getallen zijn een meting, geen gebrek: zoveel officiële
            pagina&apos;s noemen zelf geen aanvraagperiode of status. Waar de pagina
            zwijgt maar de redactie-export van vindsubsidies.nl wél een periode kent,
            toont de badge die waarde amber, mét de herkomst erbij.
            {data?.scope?.euUitgesloten && (
              <>
                {" "}
                EU-regelingen (ESF+, JTF) zitten bewust niet in dit register — de lijst is
                gemaakt voor de Nederlandse werkgever.
              </>
            )}
          </p>
        </Card>
      )}


      {/* Sorteren rechts uitgelijnd, zoals op de andere lijstschermen. */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <SortSelect
          value={sorteer ?? "venster"}
          options={SORTEER as readonly (readonly [
            NonNullable<FundingFilters["sorteer"]>,
            string,
          ])[]}
          onChange={(v) => {
            setSorteer(v);
            setPage(1);
          }}
        />
      </div>

      {lijst.error ? (
        <LoadError reason={lijst.error} onRetry={lijst.reload} />
      ) : (
        <Card style={{ overflow: "hidden", padding: 0, opacity: lijst.stale ? 0.6 : 1 }}>
          <TableHead cols={COLS} labels={LABELS} />
          {items.map((f) => {
            // W5: de serverstatus wint, voor de badge én het dimmen — één bron.
            const stand = vensterStand(f.aanvraagVenster, {
              uitExport: (f.vlaggen ?? []).includes("aanvraagperiode_uit_export"),
              status: f.status,
              statusHerkomst: f.statusHerkomst,
            });
            const regel = statusRegel(f);
            // Owner-opname 1:03: een gesloten regeling moet als HELE RIJ
            // inactief lezen, achtergrond incluis. Wél klikbaar: het venster kan
            // volgend jaar weer opengaan en de historie is waardevol — dus
            // gedempt, niet weg.
            // Reviewronde W5: dimmen volgt de badge (gesloten, of een opgeslagen
            // status met een verlopen einddatum), zodat ze nooit uiteenlopen.
            const dicht = stand.soort === "gesloten" || stand.soort === "verlopen";
            const rust = dicht ? color.surfaceQuote : "transparent";
            return (
              <div
                key={f.id}
                onClick={() => setOpen(f)}
                data-gesloten={dicht ? "ja" : undefined}
                style={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  alignItems: "center",
                  // 12px/16px zoals de andere lijsttabellen. De 20px hiervoor
                  // liep bovendien uit de pas met TableHead (0 16px), dus de
                  // kolommen stonden niet onder hun eigen kop.
                  padding: "12px 16px",
                  borderBottom: `1px solid ${color.surfaceQuote}`,
                  background: rust,
                  opacity: dicht ? 0.55 : 1,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = color.surfaceAlt)}
                onMouseLeave={(e) => (e.currentTarget.style.background = rust)}
              >
                <div style={{ minWidth: 0, paddingRight: 12 }}>
                  <div
                    style={{
                      fontSize: size.body,
                      fontWeight: weight.medium,
                      color: color.ink,
                      marginBottom: 3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.naam}
                  </div>
                  {f.trap && f.trap.waarde !== "bevestigd" && (
                    <div
                      data-trap-label={f.trap.waarde}
                      title={f.trap.uitleg}
                      style={{
                        fontSize: size.label,
                        fontWeight: weight.medium,
                        color: TRAP_KLEUR[f.trap.waarde].tekst,
                        marginBottom: 2,
                      }}
                    >
                      {f.trap.label}
                    </div>
                  )}
                  <div style={{ fontSize: size.label, color: color.faint }}>
                    {f.sectorbreed
                      ? "sectorbreed"
                      : f.sectoren
                          .map((s) => labelKaart.sector?.[s] ?? s)
                          .join(", ") || geenSectorTekst(f.vlaggen)}
                    {f.aanvraagmoment === "vooraf" && (
                      <span style={{ color: color.accentAmber, fontWeight: weight.medium }}>
                        {" · vooraf aanvragen"}
                      </span>
                    )}
                    <DekkingLabel dekking={f.dekking} vlaggen={f.vlaggen} />
                  </div>
                  {/* Funding fase 3 (G1 a): ook in de lijst, niet alleen in
                      het detail. */}
                  {vondstLabel(f.vlaggen) && (
                    <div
                      data-nieuw-niet-nagekeken
                      style={{
                        fontSize: size.label,
                        color: color.accentAmber,
                        fontWeight: weight.medium,
                        marginTop: 2,
                      }}
                    >
                      {vondstLabel(f.vlaggen)}
                    </div>
                  )}
                  {/* Koppelreparatie (08-10-2026): geen scholingsregeling of
                      alleen voor werknemers. */}
                  {scopeLabel(f.scope) && (
                    <div
                      data-scope={f.scope?.waarde}
                      title={SCOPE_UITLEG}
                      style={{
                        fontSize: size.label,
                        color: color.soft,
                        fontWeight: weight.medium,
                        marginTop: 2,
                      }}
                    >
                      {scopeLabel(f.scope)}
                    </div>
                  )}
                  {regel && (
                    <div
                      data-testid="statusregel"
                      title={regel}
                      style={{
                        fontSize: size.label,
                        color: color.faint,
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {regel}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: size.meta, color: color.soft, paddingRight: 8 }}>
                  {/* 37 van de 119 titels dragen geen apart fondsdeel: daar IS de
                      regeling de algemene scholingsregeling van het fonds en is
                      naam == verstrekker. Die naam twee keer op één regel zetten
                      is ruis; het detailpaneel zegt wat er wél bekend is. */}
                  {f.verstrekker === f.naam ? (
                    <span style={{ color: color.faint }} title="de lijst noemt geen aparte verstrekker">
                      —
                    </span>
                  ) : f.verstrekker.length > 34 ? (
                    `${f.verstrekker.slice(0, 32)}…`
                  ) : (
                    f.verstrekker
                  )}
                </div>
                <div
                  style={{
                    fontSize: size.body,
                    fontWeight: weight.bold,
                    color: f.maxBedrag.bedrag === null ? color.faint : color.ink,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {f.maxBedrag.bedrag === null
                    ? "op aanvraag"
                    : `€ ${f.maxBedrag.bedrag.toLocaleString("nl-NL")}`}
                </div>
                <div>
                  <span
                    style={{
                      fontSize: size.label,
                      fontWeight: weight.medium,
                      color: stand.kleur,
                      background: stand.achtergrond,
                      padding: "3px 8px",
                      borderRadius: 4,
                      display: "inline-block",
                    }}
                  >
                    {stand.tekst}
                  </span>
                </div>
                <div style={{ fontSize: size.label, color: color.soft }}>
                  {f.thema.map((t) => labelKaart.thema?.[t] ?? t).join(", ") || "—"}
                </div>
              </div>
            );
          })}
          {!lijst.loading && trapTellingen && !trappen.includes("niet_zeker") &&
            trapTellingen.nietZeker > 0 && page >= totalPages && (
            <div
              data-testid="niet-zeker-voet"
              style={{ padding: "12px 16px", fontSize: size.label, color: color.faint }}
            >
              {trapTellingen.nietZeker}{" "}
              {trapTellingen.nietZeker === 1 ? "regeling is" : "regelingen zijn"} niet zeker of gesloten.{" "}
              <button
                type="button"
                onClick={() => {
                  setTrappen([...TRAPPEN]);
                  setPage(1);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: color.accentBlue,
                  cursor: "pointer",
                  fontSize: size.label,
                  textDecoration: "underline",
                }}
              >
                Toon ze ook
              </button>
            </div>
          )}
          {!lijst.loading && items.length === 0 && (
            <EmptyList
              reason="Geen regelingen die aan dit filter voldoen."
              onReset={() => {
                setGekozen({});
                setZoek("");
                setTrappen([...TRAPPEN]);
                setPage(1);
              }}
            />
          )}
        </Card>
      )}

      <Pager page={page} totalPages={totalPages} onPage={setPage} />

      <SlidePanel open={open !== null} onClose={() => setOpen(null)} terugLabel="Terug naar de lijst">
        {open && (
          <FundingDetail
            funding={open}
            labels={labelKaart}
            onClose={() => setOpen(null)}
          />
        )}
      </SlidePanel>
    </div>
  );
}
